import Holidays from 'date-holidays';
import { IDate, IYearMonth, DateUnitType } from '../date';
import { i18n } from '@/i18n';

/**
 * 日付を操作するためのサービス
 */
export class DateService {
  private static t = i18n.t;
  private static _holidays: Holidays;
  /**
   * Holidaysインスタンス(外部ライブラリ)を取得する
   */
  private static get holidays(): Holidays {
    if (this._holidays instanceof Holidays === false) {
      this._holidays = new Holidays();
      this._holidays.init('JP'); // TODO: 多言語対応時には動的に変更する
    }
    return this._holidays;
  }

  /**
   * もらったIYearMonthを拡張したオブジェクトの中に、引数year、monthと一致する(検索したい)年月の情報があるかを判定する
   * @param targetObj - 比較対象のIYearMonthを拡張したオブジェクト
   * @param baseObj - 比較するためのIYearMonthを拡張したオブジェクト
   */
  public static equalYearMonthInfo = <T extends IYearMonth, U extends IYearMonth>(targetObj: T, baseObj: U): boolean => {
    return targetObj.year === baseObj.year && targetObj.month === baseObj.month;
  };

  /**
   * もらったIDateを拡張したオブジェクトの中に、引数year、month、dayと一致する(検索したい)年月日の情報があるかを判定する
   * @param targetObj - 比較対象のIDateを拡張したオブジェクト
   * @param baseObj - 比較するためのIDateを拡張したオブジェクト
   */
  public static equalYearMonthDayInfo = <T extends IDate, U extends IDate>(targetObj: T, baseObj: U): boolean => {
    return targetObj.year === baseObj.year && targetObj.month === baseObj.month && targetObj.day === baseObj.day;
  };

  /**
   * 西暦、月の組み合わせで構成された文字列から、西暦と月を分離してそれぞれをnumber型にして返す
   * 入力する情報は必ず、西暦、月を6桁で表現した形のものにして渡す
   * @param yearMonthStr - 数字6桁で表現された西暦、月の情報の文字列
   * @returns 西暦、月を分離してnumber型にcast
   */
  public static parseStringYearMonth = (yearMonthStr: string): IYearMonth => {
    const output: IYearMonth = {
      year: 0,
      month: 0,
    };
    if (yearMonthStr.length !== 6) {
      return output;
    }
    const year = Number(yearMonthStr.substring(0, 4));
    const month = Number(yearMonthStr.substring(4, 6));
    if (isNaN(year) || isNaN(month)) {
      output.year = isNaN(year) ? 0 : year;
      output.month = isNaN(month) ? 0 : month;
      return output;
    }
    output.year = year;
    output.month = month;
    return output;
  };

  /**
   * 稼働日カレンダーの年度の初期値を設定する
   * @param date - 日付情報
   */
  public static setDefaultSelectFiscalYear = (date: Date): number => {
    // 現在の年を取得
    const year = date.getFullYear();
    // 現在の月を取得
    const month = date.getMonth();
    // 月は0から始まるため、1月は0、4月は3となります。
    if (month < 3) {
      // 1月から3月の場合は、前の年度となります。
      return year - 1;
    } else {
      // それ以外の場合は現在の年となります。
      return year;
    }
  };

  /**
   * 渡された月情報に'0'をつけて文字列として返す
   * '0'をつけるのは1桁月を対象とする
   * 2桁月はそのままの形で文字列にして返す
   * @param [month] - 月情報
   * @returns 一桁の月の場合0付きで返す(例：7月->'07'、12月->'12')
   */
  public static convertMonthWithZero = (month: number | string): string => {
    let convertMonth = '';
    if (typeof month === 'string') {
      // 文字列の場合
      convertMonth = ('0' + month).slice(-2);
    } else {
      // 整数の場合
      convertMonth = ('0' + String(month)).slice(-2);
    }
    return convertMonth;
  };

  /**
   * IYearMonth型をシリアライズする
   * @param iYearMonth - 年月情報
   * @returns 年_月の文字列
   */
  public static serialize = (iYearMonth: IYearMonth): string => iYearMonth.year + '_' + iYearMonth.month;

  /**
   * 受け取った年月を指定した月分ずらした年月をIYearMonth型で返す
   * @param y  - 年
   * @param m  - 月
   * @param move_month - 動かす月数（負の数を指定すれば減算になります）
   */
  public static getShiftedIYearMonthByMonths = (y: number, m: number, move_month: number): IYearMonth => {
    const d = new Date(Number(y), Number(m) - 1, 1);
    d.setMonth(d.getMonth() + move_month);
    return {
      year: d.getFullYear(),
      month: d.getMonth() + 1,
    };
  };

  /**
   * 受け取った年月日を指定した日数分ずらした年月日をIDate型で返す
   * @param y  - 年
   * @param m  - 月
   * @param d - 日
   * @param move_days - 動かす日数（負の数を指定すれば減算になります）
   */
  public static getShiftedIYearMonthByDays = (y: number, m: number, d: number, move_days: number): IDate => {
    const date = new Date(y, m - 1, d);
    date.setDate(date.getDate() + move_days);
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
    };
  };

  /**
   * YYYY-MM-DDの文字列をDate型に変換する
   * @param dateStr - YYYY-MM-DDの文字列
   */
  public static getDate = (dateStr: string): Date => {
    const items: string[] = dateStr.split('-');
    const year = parseInt(items[0], 10);
    const month = parseInt(items[1], 10);
    const day = parseInt(items[2], 10);
    return new Date(year, month - 1, day);
  };

  /**
   * IYearMonth型の日付をYYYY/MMの文字列に変換する(1桁は0埋め, i18nなし)
   * @param date - IDate型の日付
   * @returns YYYYMMの文字列
   */
  public static convertIYearMonthToYYYYMMDivideSlash = (date: IYearMonth): string => {
    const month = String(date.month).length === 1 ? `0${date.month}` : `${date.month}`;
    return `${date.year}/${month}`;
  };

  /**
   * IYearMonth型の日付をYYYY/MMの文字列に変換する(1桁は0埋め, i18nなし)
   * @param date - IDate型の日付
   * @returns YYYYMMの文字列
   */
  public static convertIYearMonthToYYYYMM = (date: IYearMonth): string => {
    const month = String(date.month).length === 1 ? `0${date.month}` : `${date.month}`;
    return `${date.year}${month}`;
  };

  /**
   * IDate型の日付をYYYYMMDDの文字列に変換する(1桁は0埋め, i18nなし)
   * @param date - IDate型の日付
   * @returns YYYYMMDDの文字列
   */
  public static convertIYearMonthDayToYYYYMMDD = (date: IDate): string => {
    const month = String(date.month).length === 1 ? `0${date.month}` : `${date.month}`;
    const day = String(date.day).length === 1 ? `0${date.day}` : `${date.day}`;
    return `${date.year}${month}${day}`;
  };

  /**
   * IDate型の日付を文字列に変換する
   * @param date - IDate型の日付
   * @param isZeroPadding - 0埋めするかどうか
   * @returns YYYY/MM/DDの文字列
   */
  public static convertIYearMonthDayToYYYYMMDDWithi18n = (date: IDate, isZeroPadding = false): string => {
    if (isZeroPadding)
      return DateService.t('displayDate', {
        year: date.year,
        month: DateService.convertMonthWithZero(date.month),
        day: DateService.convertMonthWithZero(date.day),
      });
    return DateService.t('displayDate', { year: date.year, month: date.month, day: date.day });
  };

  /**
   * IDate型の日付をスラッシュ区切りの文字列に変換する
   * @param date - IDate型の日付
   * @param isZeroPadding - 0埋めするかどうか
   * @returns YYYY/MM/DDの文字列
   */
  public static convertIYearMonthDayToYYYYMMDDWithSlash = (date: IDate, isZeroPadding = false): string => {
    if (isZeroPadding)
      return DateService.t('displayDateSlash', {
        year: date.year,
        month: DateService.convertMonthWithZero(date.month),
        day: DateService.convertMonthWithZero(date.day),
      });
    return DateService.t('displayDateSlash', { year: date.year, month: date.month, day: date.day });
  };

  /**
   * IYearMonth型の日付をスラッシュ区切りの文字列に変換する
   * @param date - IYearMonth型の日付
   * @param isZeroPadding - 0埋めするかどうか
   * @returns YYYY/MM/DDの文字列
   */
  public static convertIYearMonthToYYYYMMWithSlash = (date: IYearMonth, isZeroPadding = false): string => {
    if (isZeroPadding) return DateService.t('displayShortDate', { year: date.year, month: DateService.convertMonthWithZero(date.month) });
    return DateService.t('displayShortDate', { year: date.year, month: date.month });
  };

  /**
   * YYYY/MM/DDの文字列をDate型に変換する
   * @param dateStr - YYYY/MM/DDの文字列
   * @returns Date型の日付
   */
  public static convertDateStrToDateWithSlash = (dateStr: string): Date => {
    const items: string[] = dateStr.split('/');
    const year = parseInt(items[0], 10);
    const month = parseInt(items[1], 10);
    const day = parseInt(items[2], 10);
    return new Date(year, month - 1, day);
  };

  /**
   * Date型文字列をYYYY-MM-DDに変換する
   * @param date - Date型の日付
   * @returns DateString
   */
  public static convertDateStrToDateWithHyphen = (date: Date): string => {
    // // 年、月、日を取り出して、二桁に揃える
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  /**
   * IDate型を比較する
   * @param date1 - 比較対象の日付1
   * @param date2 - 比較対象の日付2
   * @returns - 日付1が日付2よりも前の場合は-1、日付1が日付2よりも後の場合は1、日付1と日付2が等しい場合は0を返す
   */
  public static compareDates = (date1: IDate, date2: IDate): number => {
    // 年、月、日を比較する
    if (date1.year < date2.year) return -1;
    if (date1.year > date2.year) return 1;
    if (date1.month < date2.month) return -1;
    if (date1.month > date2.month) return 1;
    if (date1.day < date2.day) return -1;
    if (date1.day > date2.day) return 1;

    // すべての要素が等しい場合
    return 0;
  };

  /**
   * IYearMonth型を比較する
   * @param date1 - 比較対象の日付1
   * @param date2 - 比較対象の日付2
   * @returns - 日付1が日付2よりも前の場合は-1、日付1が日付2よりも後の場合は1、日付1と日付2が等しい場合は0を返す
   */
  public static compareYearMonths = (date1: IYearMonth, date2: IYearMonth): number => {
    // 年、月を比較する
    if (date1.year < date2.year) return -1;
    if (date1.year > date2.year) return 1;
    if (date1.month < date2.month) return -1;
    if (date1.month > date2.month) return 1;

    // すべての要素が等しい場合
    return 0;
  };

  /**
   * unixTimeを日に変換する
   * @param time - unixTime
   */
  public static calculateDays = function (time: number): number {
    const dayUnit = 86400000;
    return time / dayUnit;
  };

  /**
   * IDate型の日付をDate型に変換する
   * @param date - IDate型の日付
   * @returns - Date型の日付
   */
  public static convertIDateToDate = (date: IDate): Date => {
    return new Date(date.year, date.month - 1, date.day);
  };

  /**
   * IYearMonth型の日付をDate型に変換する
   * @param yearMonth - IYearMonth型の日付
   * @returns - Date型の日付
   */
  public static convertIYearMonthToDate = (yearMonth: IYearMonth): Date => {
    return new Date(yearMonth.year, yearMonth.month - 1);
  };

  /**
   * Date型の日付をIDate型に変換する
   * @param date - Date型の日付
   * @returns IDate型の日付
   */
  public static convertDateToIDate = (date: Date): IDate => {
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
    };
  };

  /**
   * Date型の日付をIYearMonth型に変換する
   * @param date - Date型の日付
   * @returns IDate型の日付
   */
  public static convertDateToIYearMonth = (date: Date): IYearMonth => {
    date.setHours(0, 0, 0, 0);
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1, // Date型の月情報は0~11までの値で表されているので、+1する
    };
  };

  /**
   * 年月日情報からIDateオブジェクトを作成する
   * @param getYear - 年
   * @param getMonth - 月
   * @param getDay - 日
   * @returns IDateオブジェクト
   */
  public static convertYearMonthDayToIDate = (getYear: number, getMonth: number, getDay: number): IDate => {
    return {
      year: getYear,
      month: getMonth,
      day: getDay,
    };
  };

  /**
   * 翌日の日付を取得
   * @param date - 現在の日付
   * @returns 翌日の日付
   */
  public static getTomorrow = (date: Date): Date => {
    const tomorrow = new Date(date);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow;
  };

  /**
   * m月d日の形式で返す
   * @param date  - 日付
   * @returns m月d日の文字列
   */
  public static getDisplayMonthDay = (date: Date): string => {
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return DateService.t('displayMonthDay', { month: String(month), day: String(day) });
  };

  /**
   * y年m月d日の形式で返す
   * @param date  - 日付
   * @returns m月d日の文字列
   */
  public static getDisplayYearMonthDay = (date: Date): string => {
    const getDate = new Date(date);
    const year = getDate.getFullYear();
    const month = getDate.getMonth() + 1;
    const day = getDate.getDate();
    return DateService.t('displayDate', { year: String(year), month: String(month), day: String(day) });
  };

  /**
   * 指定された年月+-nヶ月の年月を取得する
   * @param year - 年
   * @param month - 月
   * @param updateMonthValue - 更新する月数
   */
  public static updateYearMonth = (year: number, month: number, updateMonthValue: number): { updateYear: number; updateMonth: number } => {
    const dateEvent = new Date(); // イベントとして扱いたい
    dateEvent.setDate(1); // 日情報を1日に設定（月末だと巻き上げが発生してしまうため）
    dateEvent.setFullYear(year); // 年情報と引数でもらった値に更新
    dateEvent.setMonth(month + updateMonthValue - 1); // 月情報を引数でもらった値に更新
    return { updateYear: dateEvent.getFullYear(), updateMonth: dateEvent.getMonth() + 1 };
  };
  /**
   * 指定した2つの日付の差分日数を計算する
   * 計算は A-B = diffDays を行う
   * @param dateA - 日付A
   * @param dateB - 日付B
   */
  public static calculateDiffDays = (dateA: Date, dateB: Date): number => {
    return Math.floor(this.calculateDays(dateA.getTime() - dateB.getTime()));
  };

  /**
   * 指定した2つの日付の差分日数を計算する
   * 計算は A-B = diffDays を行う
   * @param dateA - 日付A
   * @param dateB - 日付B
   */
  public static calculateDiffMonths = (dateA: Date, dateB: Date): number => {
    let firstDate: Date = dateA;
    let secondDate: Date = dateB;
    // firstDate < secondDate になるように入れ替える
    if (firstDate.getTime() > secondDate.getTime()) {
      firstDate = dateB;
      secondDate = dateA;
    }
    let months = (secondDate.getFullYear() - firstDate.getFullYear()) * 12;
    months -= firstDate.getMonth();
    months += secondDate.getMonth();
    return months;
  };

  /**
   * 指定した2つの日付の差分月数をを計算する
   * 基本的に A - B を行う
   * @param a - 日付A
   * @param b - 日付B
   * @param isAbs - 絶対値で返すか
   */
  public static calculateIYearMonthsDiff = (a: IYearMonth, b: IYearMonth, isAbs = false): number => {
    const months = (a.year - b.year) * 12 + (a.month - b.month);
    return isAbs ? Math.abs(months) : months;
  };

  /**
   * 今日以前の日付かどうかを判定する
   * @param date - 比較する日付
   * @returns 今日以前の日付かどうか
   */
  public static isBeforeToday = (date: Date): boolean => {
    return DateService.isBeforeDate(date, new Date());
  };

  /**
   * 指定した日付が比較日時以前かどうかを判定する
   * @param date - 比較する日付
   * @param compareDate - 比較日時
   * @returns 比較日時以前であればtrue
   */
  public static isBeforeDate = (date: Date, compareDate: Date): boolean => {
    // 0時に揃える
    const targetDate = new Date(date.setHours(0, 0, 0, 0));
    const compare = new Date(compareDate.setHours(0, 0, 0, 0));
    // 比較日時以前であればtrueを返す
    return targetDate.getTime() <= compare.getTime();
  };

  /**
   * 指定した日付が比較日時以前かどうかを判定する
   * @param date - 比較する日付
   * @param compareDate - 比較日時
   * @returns 比較日時以前であればtrue
   */
  public static isBeforeIDate = (date: IDate, compareDate: IDate): boolean => {
    return DateService.isBeforeDate(DateService.convertIDateToDate(date), DateService.convertIDateToDate(compareDate));
  };

  /**
   * 指定した年月が比較年月以前かどうかを判定する
   * @param date - 比較する年月
   * @param compareDate - 比較年月
   * @returns 比較年月以前であればtrue
   */
  public static isBeforeYearMonth = (date: IYearMonth, compareDate: IYearMonth): boolean => {
    return DateService.isBeforeDate(DateService.convertIYearMonthToDate(date), DateService.convertIYearMonthToDate(compareDate));
  };

  /**
   * 指定した年月が比較年月以降かどうかを判定する
   * @param date - 比較する日付
   * @param compareDate - 比較日時
   * @returns 比較日時以降であればtrue
   */
  public static isAfterDate = (date: Date, compareDate: Date): boolean => {
    // 0時に揃える
    const targetDate = new Date(date.setHours(0, 0, 0, 0));
    const compare = new Date(compareDate.setHours(0, 0, 0, 0));
    // 比較日時以前であればtrueを返す
    return targetDate.getTime() >= compare.getTime();
  };

  /**
   * 指定した年月が比較年月以降かどうかを判定する
   * @param date - 比較する日付
   * @param compareDate - 比較日時
   * @returns 比較日時以降であればtrue
   */
  public static isAfterIDate = (date: IDate, compareDate: IDate): boolean => {
    return DateService.isAfterDate(DateService.convertIDateToDate(date), DateService.convertIDateToDate(compareDate));
  };

  /**
   * 指定した年月が比較年月以降かどうかを判定する
   * @param date - 比較する年月
   * @param compareDate - 比較年月
   * @returns 比較年月以降であればtrue
   */
  public static isAfterYearMonth = (date: IYearMonth, compareDate: IYearMonth): boolean => {
    return DateService.isAfterDate(DateService.convertIYearMonthToDate(date), DateService.convertIYearMonthToDate(compareDate));
  };

  /**
   * 指定した年月がある年月区間内かどうかを判定する
   * start <= target <= end
   * @param start - 開始年月
   * @param end - 終了年月
   */

  /**
   * 今日の日付をYYYYMMDD形式で取得する
   * @returns 今日以前の日付かどうか
   */
  public static getTodayString = (): string => {
    // 今日の日付を取得する
    const today = new Date();
    // IDate型へ変換
    const dateInfo = DateService.convertDateToIDate(today);
    // YYYYMMDD形式の文字列
    return DateService.convertIYearMonthDayToYYYYMMDD(dateInfo);
  };

  /**
   * 受け取ったIYearMonthオブジェクトの一月前の情報を返す関数
   * @param targetYearMonth - 対象のIYearMonthオブジェクト
   */
  public static getBeforeYearMonth = (targetYearMonth: IYearMonth): IYearMonth => {
    const beforeDate = new Date(targetYearMonth.year, targetYearMonth.month - 1, 0);
    return {
      year: beforeDate.getFullYear(),
      month: beforeDate.getMonth() + 1,
    };
  };

  /**
   * 単位に応じた位置で丸めた日付を返す
   * @param date - 日付
   * @param dateUnit - 単位
   * @returns 丸めた日付
   */
  public static roundDisplayDate = (date: number, dateUnit: DateUnitType): number => {
    if (dateUnit === 'month') {
      // 単位が「月」の場合は小数点第二位以下を切り捨てる
      return Math.floor(date * 10) / 10;
    } else {
      // 単位が「月」以外（日、週）の場合は小数点以下切り捨てる
      return Math.floor(date);
    }
  };

  /**
   * 日付の単位をIDateUnit型に変換する。
   * IDateUnitに変換できない場合は'day'を返す。
   * @param unit - 単位
   * @returns IDateUnit型の単位
   */
  public static convertToIDateUnit = (unit: string): DateUnitType => {
    const units: { [key: string]: DateUnitType } = {
      day: 'day',
      week: 'week',
      month: 'month',
    };
    return units[unit] || 'unknown';
  };

  /**
   * 日付が祝日かどうかを判定する
   * @param date - 日付
   */
  public static isHoliday = (date: IDate | Date): boolean => {
    if (date instanceof Date) {
      return this.holidays.isHoliday(date) instanceof Array;
    }
    return this.holidays.isHoliday(`${date.year}-${date.month}-${date.day}`) instanceof Array;
  };

  /**
   * unixTimeをIDate型に変換する
   * **unixTimeが秒単位であることに注意**
   * TODO: 240806 - corePagesModule/BaseTableContent/utils/dateFormat.ts で同名の関数があるので整備する
   * @param unixTime - unixTime（秒）
   */
  public static convertUnixTimeToIDate = (unixTime: number): IDate => {
    return this.convertDateToIDate(new Date(unixTime * 1000));
  };

  /**
   * UNIXタイムスタンプ（ミリ秒）をIDate型に変換する
   * @param timestamp - UNIXタイムスタンプ（ミリ秒）
   * @returns IDate型の日付情報
   */
  public static convertMSUnixTimeToIDate = (timestamp: number): IDate => {
    return this.convertDateToIDate(new Date(timestamp));
  };

  /**
   * IDate型をunixTimeに変換する
   * @param date - IDate型の日付データ
   * @returns - unixTime(ミリ秒)
   */
  public static convertIDateToUnixTime = (date: IDate): number => {
    return new Date(date.year, date.month - 1, date.day).getTime();
  };

  /**
   * バックエンドから来たunixTime(UTC)のYMDを維持した状態で、Date型(LocalTime)に変換する
   * unixTimeがundefinedの場合は現在時刻を返す
   * @param unixTime - unixTime
   */
  public static convertUnixTimeToDate = (unixTime?: number): Date => {
    if (unixTime === undefined) {
      return new Date();
    }
    const dateString = new Date(unixTime * 1000).toString();
    return new Date(dateString);
  };

  /**
   * バックエンドから来たunixTime(UTC)のYMDを維持した状態で、Date型(LocalTime)に変換する
   * unixTimeがundefinedの場合は現在時刻を返す
   * @param unixTime - unixTime
   */
  public static convertOldUnixTimeToDate = (unixTime?: number): Date => {
    if (unixTime === undefined) {
      return new Date();
    }
    const date = new Date(unixTime * 1000);
    return new Date(date.getTime() + date.getTimezoneOffset() * 60 * 1000);
  };

  /**
   * 月から引数で与えた分の計算を行った結果を返す
   * @param plusMonth - 計算対象の月の距離
   */
  public static addMonthsToCurrentDate = (plusMonth: number): IDate => {
    const now = new Date(Date.now());
    const year = now.getFullYear();
    const month = now.getMonth();
    const date = now.getDate();
    const dateObj = new Date(year, month + plusMonth, 1);
    const result: IDate = {
      year: dateObj.getFullYear(),
      month: dateObj.getMonth() + 1,
      day: dateObj.getDate(),
    };

    return result;
  };

  /**
   * get formatted month
   * @param value - get formatted month
   */
  public static getFormattedMonth = (value: number): string => {
    const monthNumber = DateService.addMonthsToCurrentDate(value).month;
    return `${monthNumber}月`;
  };

  /**
   * バックエンドで使う日付フォーマットに変換する（日付のみ、時分秒は0埋め）
   * @param date - 日付
   */
  public static formatDateToISOString = (date: Date): string => {
    // 年月日
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // 月は0-11なので+1する
    const day = String(date.getDate()).padStart(2, '0');

    // UTC形式の文字列を作成
    return `${year}-${month}-${day} 00:00:00Z`;
  };

  /**
   * IDateオブジェクトをバックエンドで使う日付フォーマットに変換する
   * @param date - 日付
   */
  public static formatIDateToISOString = (date: IDate): string => {
    // IDate型をDate型に変換してからフォーマットする
    return this.formatDateToISOString(this.convertIDateToDate(date));
  };

  /**
   * 2つのIYearMonthの中間部分のIYearMonthを含めた配列を返す
   * @param start - 開始年月
   * @param end - 終了年月
   */
  public static getYearMonthsBetween = (start: IYearMonth, end: IYearMonth): IYearMonth[] => {
    const result: IYearMonth[] = [];
    let current = { ...start };
    while (this.compareYearMonths(current, end) <= 0) {
      result.push({ ...current });
      current = this.getShiftedIYearMonthByMonths(current.year, current.month, 1);
    }
    return result;
  };
  /**
   * 年月のリストをYYYYMMの文字列のリストに変換する
   * @param start - 開始年月
   * @param end - 終了年月
   * @returns YYYYMMの文字列のリスト
   */
  public static getYearMonthStringList = (start: IYearMonth, end: IYearMonth): string[] => {
    return this.getYearMonthsBetween(start, end).map((ym) => this.convertIYearMonthToYYYYMMWithSlash(ym, true));
  };

  /**
   * 文字列情報が日付として有効かをチェックする
   * 対応フォーマットはYYYY-MM-DDまたはYYYY/MM/DD
   * @param dateString - 日付文字列
   */
  public static isValidDateFormat(dateString: string): boolean {
    // フォーマットチェック用の正規表現
    const dateRegex = /^(?:\d{4}[-/]\d{2}[-/]\d{2})$/;

    // フォーマットが正しいかチェック
    if (!dateRegex.test(dateString)) {
      return false;
    }

    // 日付として有効かをチェック
    const [year, month, day] = dateString.split(/[-/]/).map(Number);
    const date = new Date(year, month - 1, day);

    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  }

  /**
   * エクセルの日付フォーマットとして正しいかをチェックする
   * @param dateString - 日付文字列
   */
  public static isValidExcelDateFormat(dateString: string): boolean {
    // 正規表現で「M/D/Y」または「M/D/YY」の形式をチェック
    const regex = /^(0?[1-9]|1[0-2])\/(0?[1-9]|[12][0-9]|3[01])\/(\d{1,2})$/;

    const match = dateString.match(regex);
    if (!match) {
      // フォーマットが間違っている場合
      return false;
    }

    // 現在の年を基準に適切な西暦を計算
    const currentYear = new Date().getFullYear();
    const currentCentury = Math.floor(currentYear / 100) * 100;

    const month = parseInt(match[1], 10);
    const day = parseInt(match[2], 10);
    const year = parseInt(match[3], 10);

    // 年が1桁または2桁の場合の補完
    const fullYear = year < 100 ? year + (year <= currentYear % 100 ? currentCentury : currentCentury - 100) : year;

    // 実際の日付が存在するか確認
    const date = new Date(fullYear, month - 1, day);
    return date.getFullYear() === fullYear && date.getMonth() === month - 1 && date.getDate() === day;
  }

  /**
   * Excelのシリアル日付を年月日に変換する
   * @param serial - Excelのシリアル値
   * @returns 変換できた場合は年月日、変換できない場合はundefined
   */
  public static convertExcelSerialToDate = (serial: number): IDate | undefined => {
    if (!Number.isFinite(serial)) return;

    const normalizedSerial = Math.floor(serial);
    if (normalizedSerial <= 0) return;

    // Excelの1900日付システムは1899-12-30を基準にする
    const baseTime = Date.UTC(1899, 11, 30);
    const date = new Date(baseTime + normalizedSerial * 24 * 60 * 60 * 1000);

    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate(),
    };
  };

  /**
   * アンダースコア繋ぎの年月形式の文字列かどうかを判定する関数
   * 判定方法は、'_'分割した際に前後のデータでDateオブジェクトを作成できるかで判定する
   * @param yearMonthString - 判定する文字列
   * @returns boolean - 年月形式なら true、それ以外なら false
   */
  public static isYearMonthUnderscoreFormat = (yearMonthString: string): boolean => {
    // 引数を'_'で分割
    const parts = yearMonthString.split('_');

    // '_'で2つに分割できない場合は false
    if (parts.length !== 2) {
      return false;
    }

    // 年月それぞれの情報を取得
    const [yearPart, monthPart] = parts;

    // 年と月を数値に変換
    const year = Number(yearPart);
    const month = Number(monthPart);

    // 年と月が整数であることを確認
    // NOTE: 少数なども許容しないので、整数であるかを確認している
    if (!Number.isInteger(year) || !Number.isInteger(month)) {
      return false;
    }

    // Dateオブジェクトを作成し、年月が一致するか確認
    const date = new Date(year, month - 1); // JavaScriptの月は0から始まる

    return date.getFullYear() === year && date.getMonth() === month - 1;
  };

  /**
   * アンダースコア繋ぎの年月日形式の文字列かどうかを判定する関数
   * 判定方法は、'_'分割した際に前後のデータでDateオブジェクトを作成できるかで判定する
   * @param yearMonthDayString - 判定する文字列
   * @returns boolean - 年月日形式なら true、それ以外なら false
   */
  public static isYearMonthDayUnderscoreFormat = (yearMonthDayString: string): boolean => {
    const parts = yearMonthDayString.split('_');
    if (parts.length !== 3) {
      return false;
    }

    const [yearPart, monthPart, dayPart] = parts;
    const year = Number(yearPart);
    const month = Number(monthPart);
    const day = Number(dayPart);

    if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
      return false;
    }

    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  };

  /**
   * 受け取ったDate型変数からmoveMonthで指定した月分動かした年月をDate型で返す関数
   * Note: 日付が1日に固定されます。
   * @param dt - 対象のDate
   * @param moveMonth - 動かす月数（負の数を指定すれば減算になります）
   * @returns Date - 動かした後のDate型変数
   */
  public static getShiftedDateByMonths = (dt: Date, moveMonth: number): Date => {
    const resDate = new Date(dt);
    resDate.setDate(1);
    resDate.setMonth(dt.getMonth() + moveMonth);
    return resDate;
  };

  /**
   * 現在の年月からmoveMonthで指定した月分動かした年月をDate型で返す関数
   * Note: 日付が1日に固定されます。
   * @param moveMonth - 動かす月数（負の数を指定すれば減算になります）
   * @returns Date - 動かした後のDate型変数
   */
  public static getShiftedDateFromNowDateByMonths = (moveMonth: number): Date => {
    return this.getShiftedDateByMonths(new Date(), moveMonth);
  };

  /**
   * 現在の年月からmoveMonthで指定した月分動かした年月をIYearMonth型で返す関数
   * @param moveMonth - 動かす月数（負の数を指定すれば減算になります）
   * @returns Date - 動かした後のDate型変数
   */
  public static getShiftedIYearMonthFromNowDateByMonths = (moveMonth: number): IYearMonth => {
    const dt = new Date();
    return this.getShiftedIYearMonthByMonths(dt.getFullYear(), dt.getMonth() + 1, moveMonth);
  };

  /**
   * Date型の日付をYYYYMMの文字列に変換する
   * @param date - Date型の日付
   */
  public static formatDateToYYYYMM = (date: Date) => date.toISOString().slice(0, 7).replace('-', '');

  /**
   * 指定した月数分の年月ラベルを取得する
   * @param start - 始点
   * @param range - 範囲
   */
  public static getMonthRangeList = (start = 1, range = 1): string[] => {
    const result: string[] = [];
    for (let month = start; month <= range; month++) {
      result.push(month + DateService.t('months'));
    }
    return result;
  };

  /**
   * Date型の日時を YYYY/MM/DD HH:MM (ja)に変換する
   * @param date - Date型の日時情報
   * @returns - i18nされた 年月日 + HH:MMの文字列
   */
  public static convertDateToYYYYMMDDHHMM = (date: Date) =>
    `${DateService.t('displayDateSlash', {
      year: date.getFullYear().toString(),
      month: (date.getMonth() + 1).toString().padStart(2, '0'),
      day: date.getDate().toString().padStart(2, '0'),
    })} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;

  /**
   * ----/--/-- --:-- (ja)を取得する
   * @returns - i18nされた 年月日 + HH:MMが全てハイフンになった情報
   */
  public static convertDateToYYYYMMDDHHMMAllHyphen = () =>
    `${DateService.t('displayDateSlash', {
      year: '----',
      month: '--',
      day: '--',
    })} --:--`;

  /**
   * バックエンドから来たunixTime(UTC)のYMDを維持した状態で、Date型(LocalTime)に変換する
   * なお、BEでmsではなくsで管理している場合はconvertUnixTimeToDateを使用してください
   * unixTimeがundefinedの場合は現在時刻を返す
   * @param MSUnixTime - unixTime(ms)
   */
  public static convertMSUnixTimeToDate = (MSUnixTime?: number): Date => {
    if (MSUnixTime === undefined) {
      return new Date();
    }
    const dateString = new Date(MSUnixTime).toString();
    return new Date(dateString);
  };

  /**
   * UNIXタイムスタンプ(ms)を YYYY/MM/DD 形式の文字列に変換する
   * @param timestamp - UNIXタイムスタンプ(ms)
   * @returns YYYY/MM/DD形式の文字列
   */
  public static convertMSUnixTimeToYYYYMMDDSlash = (timestamp: number): string => {
    const date = new Date(timestamp);
    const iDate = DateService.convertDateToIDate(date);
    return DateService.convertIYearMonthDayToYYYYMMDDWithSlash(iDate, true);
  };

  /**
   * UNIXタイムスタンプ(ms)を YYYY/MM/DD hh:mm  形式の文字列に変換する
   * @param timestamp - UNIXタイムスタンプ(ms)
   * @returns YYYY/MM/DD形式の文字列
   */
  public static convertMSUnixTimeToYYYYMMDDhhmm = (timestamp: number): string => {
    const date = new Date(timestamp);
    const iDate = DateService.convertDateToIDate(date);
    return (
      DateService.convertIYearMonthDayToYYYYMMDDWithSlash(iDate, true) +
      ` ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
    );
  };

  /**
   * 現在の月との距離を指定した範囲の年月から取得する
   * @param startYearMonth - 開始年月
   * @param endYearMonth - 終了年月
   * @returns 月の距離のリスト
   */
  public static getMonthDistanceFromCurrentMonthList = (startYearMonth: IYearMonth, endYearMonth: IYearMonth): number[] => {
    // 現在の年月を取得
    const currentYearMonth = DateService.convertDateToIYearMonth(new Date());
    // 年月情報を月単位に変換する
    const currentTotal = currentYearMonth.year * 12 + currentYearMonth.month;
    const startTotal = startYearMonth.year * 12 + startYearMonth.month;
    const endTotal = endYearMonth.year * 12 + endYearMonth.month;

    const result: number[] = [];

    // 開始から終了までの月数を計算
    for (let total = startTotal; total <= endTotal; total++) {
      // 月数を計算
      result.push(total - currentTotal);
    }

    // 結果を昇順にソートして返す
    return result.sort((a, b) => a - b);
  };

  /**
   * 指定年月の月末日を `IDate` で返す
   * @param date - 対象年月
   * @returns 対象年月の最終日
   */
  public static convertIYearMonthToLastDateIDate = (date: IYearMonth): IDate => {
    return this.convertDateToIDate(new Date(date.year, date.month, 0));
  };

  /**
   * IYearMonthをYYYY-MMの文字列に変換する
   * @param date - IYearMonth型の年月情報
   * @returns - YYYY-MM の文字列
   */
  public static convertIYearMonthToDateStr = (date: IYearMonth): string => {
    return `${date.year}-${date.month}`;
  };

  /**
   * IYearMonthのバリデーションチェック
   * @param date - 日付
   */
  public static validIYearMonth = (date: IYearMonth): boolean => {
    return (
      date != null && date.year != null && date.month != null && 1 <= date.year && date.year <= 9999 && 1 <= date.month && date.month <= 12
    );
  };
}
